/**
 * RATE MANAGEMENT — admin tool for editing villa rates per villa per date.
 *
 * Live, wired up to the backend at /api/admin/rate-management/* +
 * /api/admin/villa-overrides + /api/admin/rate-overrides.
 *
 * Existing pre-bookings remain mathematically immune to any rate change here,
 * because every booking already snapshots `nightly_details` at booking creation.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { FiArrowLeft, FiAlertTriangle, FiX, FiUsers, FiUserPlus, FiCheck, FiPower, FiChevronDown, FiChevronRight, FiRotateCcw } from 'react-icons/fi';
import { useSettings } from '../context/SettingsContext';
import Villa12BhkPricingModePanel from '../components/Villa12BhkPricingModePanel';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const YEARS = [2026, 2027, 2028];
const FIELD_LABELS = {
  base_price: 'Weekday Rate',
  weekend_price: 'Weekend Rate (Fri-Sun)',
  capacity: 'Max Guests',
  extra_guest_charge: 'Extra Guest /night',
};

const fmtDate = (y, m, d) => `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

const RateManagement = () => {
  const [year, setYear] = useState(2026);
  const [monthIdx, setMonthIdx] = useState(new Date().getMonth());
  const [villas, setVillas] = useState([]);
  const [villaId, setVillaId] = useState(null);
  const [overrides, setOverrides] = useState({}); // {YYYY-MM-DD: rate}
  const [holidays, setHolidays] = useState({});
  const [status, setStatus] = useState(null); // {enabled, ...}
  const [busy, setBusy] = useState(false);
  const [editDay, setEditDay] = useState(null);
  const [draftRate, setDraftRate] = useState('');
  const [editingField, setEditingField] = useState(null); // {villa_id, field}
  const [editingFieldValue, setEditingFieldValue] = useState('');
  const [monthDefaults, setMonthDefaults] = useState([]); // [{month, base_price, weekend_price, capacity, extra_guest_charge, has_override}]
  const [showMonthDefaults, setShowMonthDefaults] = useState(false);
  const [editingMonth, setEditingMonth] = useState(null); // {month, field}
  const [editingMonthValue, setEditingMonthValue] = useState('');
  const [discountPct, setDiscountPct] = useState(15);
  const [discountEnabled, setDiscountEnabled] = useState(true);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [discountDraft, setDiscountDraft] = useState('');
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();
  const { refresh: refreshGlobalSettings } = useSettings();

  const villa = villas.find(v => v.villa_id === villaId);

  // Auth-guard
  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (!u.is_admin) navigate('/login');
  }, [navigate]);

  // Load status + villas + holidays + discount setting on mount
  const refreshAll = useCallback(async () => {
    setBusy(true);
    try {
      const [s, v, h, d] = await Promise.all([
        api.get('/admin/rate-management/status'),
        api.get('/admin/villa-overrides'),
        api.get('/admin/holidays'),
        api.get('/admin/discount-settings'),
      ]);
      setStatus(s.data);
      setVillas(v.data);
      setHolidays(h.data);
      if (typeof d.data?.night_discount_pct === 'number') {
        setDiscountPct(d.data.night_discount_pct);
      }
      if (typeof d.data?.enabled === 'boolean') {
        setDiscountEnabled(d.data.enabled);
      }
      if (!villaId && v.data.length) setVillaId(v.data[0].villa_id);
    } catch (err) {
      setToast({ type: 'error', text: err.response?.data?.detail || 'Failed to load' });
    }
    setBusy(false);
  }, [villaId]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Re-fetch per-date overrides whenever villa / month / year changes
  useEffect(() => {
    if (!villaId) return;
    let ignore = false;
    api.get(`/admin/rate-overrides?villa_id=${villaId}&month=${monthIdx + 1}&year=${year}`)
      .then(res => {
        if (ignore) return;
        const map = {};
        (res.data?.overrides || []).forEach(o => { map[o.date] = o.nightly_rate; });
        setOverrides(map);
      })
      .catch(() => { if (!ignore) setOverrides({}); });
    return () => { ignore = true; };
  }, [villaId, monthIdx, year, status?.enabled]);

  // Re-fetch month defaults whenever villa / year changes
  useEffect(() => {
    if (!villaId) return;
    let ignore = false;
    api.get(`/admin/month-overrides?villa_id=${villaId}&year=${year}`)
      .then(res => {
        if (ignore) return;
        setMonthDefaults(res.data?.months || []);
      })
      .catch(() => { if (!ignore) setMonthDefaults([]); });
    return () => { ignore = true; };
  }, [villaId, year, status?.enabled]);

  const flash = (text, type = 'success') => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      await api.post('/admin/rate-management/enable');
      flash('Rate Management enabled');
      await refreshAll();
    } catch (err) {
      flash(err.response?.data?.detail || 'Enable failed', 'error');
    }
    setBusy(false);
  };

  const handleDisable = async () => {
    const msg = `KILL SWITCH — Cancel Rate Management?

This will:
  • Delete ALL villa overrides (weekday / weekend / max / extra rates)
  • Delete ALL per-month and per-date custom rates
  • Revert villas to their original ${status?.baseline_snapshot_at ? new Date(status.baseline_snapshot_at).toLocaleDateString('en-IN') : '03-Jun-2026'} values
  • Hide the Rate Management tool

Existing guest bookings are NOT affected — they keep their booked rates.

Continue?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const res = await api.post('/admin/rate-management/disable');
      flash(`Reverted: ${res.data.reverted_villa_overrides} villa + ${res.data.reverted_rate_overrides} date overrides removed`);
      await refreshAll();
    } catch (err) {
      flash(err.response?.data?.detail || 'Disable failed', 'error');
    }
    setBusy(false);
  };

  // Inline edit of villa-level field
  const startFieldEdit = (vid, field, current) => {
    setEditingField({ villa_id: vid, field });
    setEditingFieldValue(String(current));
  };
  const saveFieldEdit = async () => {
    const amt = parseInt(editingFieldValue, 10);
    if (!Number.isFinite(amt) || amt < 0) {
      flash('Enter a valid number', 'error'); return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/villa-overrides/${editingField.villa_id}`, { [editingField.field]: amt });
      flash(`${FIELD_LABELS[editingField.field]} updated`);
      setEditingField(null);
      await refreshAll();
    } catch (err) {
      flash(err.response?.data?.detail || 'Save failed', 'error');
    }
    setBusy(false);
  };

  // Inline edit of per-month default
  const startMonthEdit = (month, field, current) => {
    setEditingMonth({ month, field });
    setEditingMonthValue(String(current));
  };
  const saveMonthEdit = async () => {
    const amt = parseInt(editingMonthValue, 10);
    if (!Number.isFinite(amt) || amt < 0) { flash('Enter a valid number', 'error'); return; }
    setBusy(true);
    try {
      await api.put(`/admin/month-overrides/${villaId}/${year}/${editingMonth.month}`, { [editingMonth.field]: amt });
      const res = await api.get(`/admin/month-overrides?villa_id=${villaId}&year=${year}`);
      setMonthDefaults(res.data?.months || []);
      flash(`${MONTHS_FULL[editingMonth.month - 1]} ${FIELD_LABELS[editingMonth.field]} updated`);
      setEditingMonth(null);
    } catch (err) {
      flash(err.response?.data?.detail || 'Save failed', 'error');
    }
    setBusy(false);
  };
  const resetMonthOverride = async (month) => {
    if (!window.confirm(`Reset ${MONTHS_FULL[month - 1]} ${year} to villa defaults?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/month-overrides/${villaId}/${year}/${month}`);
      const res = await api.get(`/admin/month-overrides?villa_id=${villaId}&year=${year}`);
      setMonthDefaults(res.data?.months || []);
      flash(`${MONTHS_FULL[month - 1]} reset to villa defaults`);
    } catch (err) {
      flash(err.response?.data?.detail || 'Reset failed', 'error');
    }
    setBusy(false);
  };

  // 2nd-night-onwards discount % editor
  const openDiscountEditor = () => {
    setEditingDiscount(true);
    setDiscountDraft(String(discountPct));
  };
  const cancelDiscountEditor = () => {
    setEditingDiscount(false);
    setDiscountDraft('');
  };
  const saveDiscountEdit = async () => {
    const pct = parseInt(discountDraft, 10);
    if (!Number.isFinite(pct) || pct < 0 || pct > 90) {
      flash('Discount % must be an integer between 0 and 90', 'error');
      return;
    }
    setBusy(true);
    try {
      const res = await api.put('/admin/discount-settings', { night_discount_pct: pct });
      setDiscountPct(res.data.night_discount_pct);
      setEditingDiscount(false);
      // Refresh the global SettingsContext so Header + other components re-fetch immediately
      refreshGlobalSettings();
      flash(`2nd-night-onwards discount updated to ${res.data.night_discount_pct}% — applies to all future bookings from now`);
    } catch (err) {
      flash(err.response?.data?.detail || 'Save failed', 'error');
    }
    setBusy(false);
  };

  const toggleDiscountEnabled = async () => {
    const next = !discountEnabled;
    if (!next && !window.confirm('Turn OFF the multi-night discount? Guests will stop seeing 15% OFF badges + strips, and every villa reverts to full nightly rate.')) return;
    setBusy(true);
    try {
      const res = await api.put('/admin/discount-settings', { enabled: next });
      setDiscountEnabled(res.data.enabled);
      refreshGlobalSettings();
      flash(next
        ? 'Multi-night discount turned ON — banners, badges and pricing calc are now live.'
        : 'Multi-night discount turned OFF — banners hidden site-wide, full nightly rates applied.');
    } catch (err) {
      flash(err.response?.data?.detail || 'Toggle failed', 'error');
    }
    setBusy(false);
  };

  // Build month grid
  const grid = useMemo(() => {
    const firstDay = new Date(year, monthIdx, 1);
    const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay.getDay(); i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, monthIdx, d);
      const weekday = date.getDay();
      const isWeekend = weekday === 5 || weekday === 6 || weekday === 0;
      const key = fmtDate(year, monthIdx + 1, d);
      cells.push({ d, key, weekday, isWeekend, holiday: holidays[key] });
    }
    return cells;
  }, [year, monthIdx, holidays]);

  const baseRateFor = (cell) => cell.isWeekend ? villa?.weekend_price : villa?.base_price;
  const effectiveRate = (cell) => overrides[cell.key] ?? baseRateFor(cell);

  const openDayEdit = (cell) => {
    setEditDay(cell);
    setDraftRate(String(effectiveRate(cell)));
  };

  const saveDayEdit = async () => {
    const amt = parseInt(draftRate, 10);
    if (!Number.isFinite(amt) || amt <= 0) { flash('Enter a valid amount > 0', 'error'); return; }
    setBusy(true);
    try {
      await api.put(`/admin/rate-overrides/${villaId}/${editDay.key}`, { nightly_rate: amt });
      setOverrides({ ...overrides, [editDay.key]: amt });
      flash(`Saved Rs.${amt.toLocaleString()} for ${editDay.key}`);
      setEditDay(null);
    } catch (err) {
      flash(err.response?.data?.detail || 'Save failed', 'error');
    }
    setBusy(false);
  };

  const clearDayOverride = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/rate-overrides/${villaId}/${editDay.key}`);
      const next = { ...overrides }; delete next[editDay.key];
      setOverrides(next);
      flash(`Cleared override for ${editDay.key}`);
      setEditDay(null);
    } catch (err) {
      flash(err.response?.data?.detail || 'Clear failed', 'error');
    }
    setBusy(false);
  };

  if (status && !status.enabled) {
    // Onboarding screen — explain what enabling does
    return (
      <div className="pt-20 min-h-screen bg-[#0A0E17] text-white" data-testid="rate-mgmt-disabled-state">
        <section className="py-12 md:py-20">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <Link to="/admin" className="text-white/40 hover:text-white text-xs flex items-center gap-1 justify-center mb-8">
              <FiArrowLeft className="w-4 h-4" /> Back to Admin Dashboard
            </Link>
            <h1 className="text-3xl md:text-4xl font-light mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Rate <span className="text-[#F57C20]">Management</span> — Not Active
            </h1>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">
              You haven&apos;t enabled custom pricing yet. Right now your villa rates come from the hard-coded defaults in the code.
              Enable to start setting per-villa or per-date custom rates.
            </p>
            <div className="bg-white/5 rounded-lg p-5 mb-6 text-left text-xs text-white/60 space-y-1.5">
              <p className="text-white font-semibold mb-2">Enabling will:</p>
              <p>1. Snapshot current villa rates as the immutable baseline (used by the kill switch)</p>
              <p>2. Let you edit weekday / weekend / max guests / extra-guest rate per villa</p>
              <p>3. Let you set per-DATE custom rates for festivals, holidays, peak weekends</p>
              <p>4. Apply instantly across homepage, villa pages, booking flow, payment</p>
              <p className="text-green-400">✓ Existing bookings remain untouched — they keep their booked rates</p>
            </div>
            <button
              onClick={handleEnable}
              disabled={busy}
              data-testid="enable-rate-mgmt-btn"
              className="bg-[#F57C20] text-white px-6 py-3 rounded-lg text-sm font-bold hover:bg-[#e06b15] disabled:opacity-50 transition-colors"
            >
              {busy ? 'Enabling…' : 'Enable Rate Management'}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="rate-management-page" className="pt-20 min-h-screen bg-[#0A0E17] text-white">
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* Header */}
          <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
            <div>
              <Link to="/admin" className="text-white/40 hover:text-white text-xs flex items-center gap-1 mb-3">
                <FiArrowLeft className="w-4 h-4" /> Back to Admin Dashboard
              </Link>
              <h1 className="text-3xl md:text-4xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
                Rate <span className="text-[#F57C20]">Management</span>
              </h1>
              <p className="text-white/40 text-xs mt-1">
                Status: <span className="text-green-400 font-semibold">● Active</span>
                {status?.baseline_snapshot_at && (
                  <span className="text-white/30 ml-2">Baseline captured {new Date(status.baseline_snapshot_at).toLocaleDateString('en-IN')}</span>
                )}
              </p>
            </div>
            <button
              onClick={handleDisable}
              disabled={busy}
              data-testid="disable-rate-mgmt-btn"
              className="bg-red-500/15 text-red-300 border border-red-500/30 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-red-500/25 transition-colors flex items-center gap-2"
            >
              <FiPower className="w-3.5 h-3.5" /> Cancel & Revert
            </button>
          </div>

          {/* 12 BHK pricing mode toggle — manual vs auto-sum of 3+4+5 */}
          <Villa12BhkPricingModePanel />

          {/* Villa selector */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="villa-picker">
            {villas.map(v => (
              <button
                key={v.villa_id}
                onClick={() => setVillaId(v.villa_id)}
                data-testid={`villa-pill-${v.villa_id}`}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                  villaId === v.villa_id
                    ? 'bg-[#F57C20] text-white shadow-lg'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {v.short_name} <span className="text-[10px] opacity-60 ml-1">({v.bedrooms}BHK)</span>
                {v.has_override && <span className="ml-1.5 text-[8px] text-green-400">★</span>}
              </button>
            ))}
          </div>

          {/* 2nd-night-onwards Discount % Editor (GLOBAL — applies to ALL villas & future bookings only) */}
          <div className="bg-[#1B2432] rounded-xl p-5 mb-6" data-testid="discount-settings-card">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1">
                <p className="text-[#F57C20] text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Multi-night discount</p>
                <p className="text-white text-sm">Stay Longer, Pay Less — % off on every night from the 2nd onwards</p>
                <p className="text-[#9CA3AF] text-[11px] mt-1">Applies to all villas. Change reflects instantly on the live site &amp; new bookings. Existing bookings remain untouched.</p>
              </div>
              {/* Master ON / OFF toggle */}
              <button
                onClick={toggleDiscountEnabled}
                disabled={busy}
                data-testid="discount-master-toggle"
                aria-label={discountEnabled ? 'Turn discount off' : 'Turn discount on'}
                className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors disabled:opacity-50 flex-shrink-0 ${discountEnabled ? 'bg-green-500/20 text-green-400 border border-green-500/40' : 'bg-red-500/20 text-red-400 border border-red-500/40'}`}
              >
                <span className={`w-2 h-2 rounded-full ${discountEnabled ? 'bg-green-400' : 'bg-red-400'}`} />
                {discountEnabled ? 'On' : 'Off'}
              </button>
            </div>
            {!discountEnabled && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2.5 text-[11px] text-yellow-300 mb-3" data-testid="discount-off-notice">
                Discount is currently <span className="font-bold">OFF</span>. Guests see full nightly rates on every villa. No 15% badges, strips or Multi-Night Offer cards are shown site-wide.
              </div>
            )}
            <div className={`bg-[#232A3D] rounded-lg p-4 flex items-center justify-between gap-3 ${discountEnabled ? '' : 'opacity-60'}`}>
              <div>
                <p className="text-[#9CA3AF] text-[10px] uppercase tracking-widest mb-1">2nd Night+ Discount</p>
                {editingDiscount ? (
                  <div className="flex items-center gap-2" data-testid="discount-edit-row">
                    <input
                      type="number"
                      value={discountDraft}
                      onChange={e => setDiscountDraft(e.target.value)}
                      min="0"
                      max="90"
                      autoFocus
                      className="bg-[#0F1420] text-white text-2xl font-bold px-3 py-2 rounded w-24 outline-none border border-[#F57C20]"
                      data-testid="discount-input"
                    />
                    <span className="text-white text-2xl font-bold">%</span>
                    <button onClick={saveDiscountEdit} disabled={busy} data-testid="discount-save-btn"
                      className="bg-[#F57C20] text-white px-4 py-2 text-xs font-semibold rounded hover:bg-[#E06A10] disabled:opacity-50">
                      Save
                    </button>
                    <button onClick={cancelDiscountEditor} data-testid="discount-cancel-btn"
                      className="text-[#9CA3AF] px-3 py-2 text-xs hover:text-white">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-white text-2xl font-bold" data-testid="discount-current-value">{discountPct}%</p>
                    <button onClick={openDiscountEditor} data-testid="discount-edit-btn"
                      className="text-[#F57C20] text-xs mt-1 hover:underline">
                      Edit
                    </button>
                  </>
                )}
              </div>
              <div className="text-right text-[#9CA3AF] text-[11px] max-w-[220px]">
                <p><span className="text-white font-semibold">1st night</span> always at actual rate.</p>
                <p className="mt-1"><span className="text-white font-semibold">2nd+ night</span> at {discountPct}% off, auto-applied.</p>
              </div>
            </div>
          </div>

          {/* Villa defaults editor */}
          {villa && (
            <div className="bg-[#1B2432] rounded-xl p-5 mb-6" data-testid="villa-defaults">
              <div className="flex items-end justify-between gap-3 mb-4">
                <div>
                  <p className="text-[#F57C20] text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Villa defaults</p>
                  <p className="text-white text-sm">{villa.short_name} — base rates & guest config</p>
                </div>
                {villa.has_override && (
                  <span className="text-green-400 text-[10px] flex items-center gap-1"><FiCheck className="w-3 h-3" /> Has overrides</span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['base_price', 'weekend_price', 'capacity', 'extra_guest_charge'].map(field => {
                  const isEditing = editingField?.villa_id === villa.villa_id && editingField?.field === field;
                  const isMoneyField = field !== 'capacity';
                  return (
                    <div key={field} className="bg-white/5 rounded-lg p-3">
                      <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1">
                        {field === 'capacity' && <FiUsers className="w-3 h-3" />}
                        {field === 'extra_guest_charge' && <FiUserPlus className="w-3 h-3" />}
                        {FIELD_LABELS[field]}
                      </p>
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            value={editingFieldValue}
                            onChange={e => setEditingFieldValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveFieldEdit(); if (e.key === 'Escape') setEditingField(null); }}
                            autoFocus
                            data-testid={`field-input-${field}`}
                            className="w-full bg-[#0A0E17] border border-[#F57C20] rounded px-2 py-1 text-white text-sm focus:outline-none"
                          />
                          <button onClick={saveFieldEdit} disabled={busy} data-testid={`field-save-${field}`} className="text-green-400 hover:text-green-300"><FiCheck className="w-4 h-4" /></button>
                          <button onClick={() => setEditingField(null)} className="text-white/40 hover:text-white"><FiX className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <>
                          <p className="text-white text-lg font-bold">{isMoneyField ? `Rs.${villa[field].toLocaleString()}` : villa[field]}</p>
                          <button
                            onClick={() => startFieldEdit(villa.villa_id, field, villa[field])}
                            data-testid={`field-edit-${field}`}
                            className="text-[#F57C20] text-[10px] mt-1 hover:underline"
                          >Edit</button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Month Defaults (collapsible) */}
          {villa && (
            <div className="bg-[#1B2432] rounded-xl mb-6 overflow-hidden" data-testid="month-defaults">
              <button
                onClick={() => setShowMonthDefaults(s => !s)}
                data-testid="month-defaults-toggle"
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.03] transition-colors"
              >
                <div className="text-left">
                  <p className="text-[#F57C20] text-[10px] tracking-[0.2em] uppercase font-semibold mb-1">Month Defaults — {year}</p>
                  <p className="text-white text-sm">Override villa defaults month-by-month {monthDefaults.some(m => m.has_override) && <span className="text-green-400 text-[10px] ml-2">★ {monthDefaults.filter(m => m.has_override).length} active</span>}</p>
                </div>
                {showMonthDefaults
                  ? <FiChevronDown className="text-white/60 w-5 h-5" />
                  : <FiChevronRight className="text-white/60 w-5 h-5" />}
              </button>
              {showMonthDefaults && (
                <div className="px-5 pb-5">
                  <p className="text-white/40 text-[10px] italic mb-3">
                    Precedence: per-date override &gt; month default &gt; villa default. Resetting reverts that month to villa defaults.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {monthDefaults.map(md => (
                      <div
                        key={md.month}
                        data-testid={`month-card-${md.month}`}
                        className={`rounded-lg p-3 border ${md.has_override ? 'bg-green-500/[0.06] border-green-500/30' : 'bg-white/5 border-white/10'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white text-sm font-semibold">
                            {MONTHS_FULL[md.month - 1]}
                            {md.has_override && <span className="ml-1.5 text-[9px] text-green-400">★</span>}
                          </p>
                          {md.has_override && (
                            <button
                              onClick={() => resetMonthOverride(md.month)}
                              disabled={busy}
                              data-testid={`month-reset-${md.month}`}
                              className="text-white/40 hover:text-red-400 transition-colors flex items-center gap-1 text-[10px]"
                              title="Reset to villa defaults"
                            >
                              <FiRotateCcw className="w-3 h-3" /> Reset
                            </button>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          {['base_price', 'weekend_price', 'capacity', 'extra_guest_charge'].map(field => {
                            const isEditing = editingMonth?.month === md.month && editingMonth?.field === field;
                            const isMoneyField = field !== 'capacity';
                            return (
                              <div key={field} className="flex items-center justify-between gap-2">
                                <span className="text-white/50 text-[10px] uppercase tracking-wider flex-1 truncate">{FIELD_LABELS[field]}</span>
                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="number"
                                      value={editingMonthValue}
                                      onChange={e => setEditingMonthValue(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') saveMonthEdit(); if (e.key === 'Escape') setEditingMonth(null); }}
                                      autoFocus
                                      data-testid={`month-input-${md.month}-${field}`}
                                      className="w-20 bg-[#0A0E17] border border-[#F57C20] rounded px-1.5 py-0.5 text-white text-xs focus:outline-none"
                                    />
                                    <button onClick={saveMonthEdit} disabled={busy} data-testid={`month-save-${md.month}-${field}`} className="text-green-400 hover:text-green-300"><FiCheck className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => setEditingMonth(null)} className="text-white/40 hover:text-white"><FiX className="w-3.5 h-3.5" /></button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => startMonthEdit(md.month, field, md[field])}
                                    data-testid={`month-edit-${md.month}-${field}`}
                                    className="text-white text-xs font-semibold hover:text-[#F57C20] transition-colors"
                                    title="Click to edit"
                                  >
                                    {isMoneyField ? `Rs.${(md[field] ?? 0).toLocaleString()}` : md[field]}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Year + month tabs */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <label className="text-white/40 text-[10px] uppercase tracking-wider">Year</label>
              <select
                value={year}
                onChange={e => setYear(parseInt(e.target.value, 10))}
                data-testid="year-select"
                className="bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-white text-xs focus:outline-none focus:border-[#F57C20]"
              >
                {YEARS.map(y => <option key={y} value={y} className="bg-[#131722]">{y}</option>)}
              </select>
            </div>
            <p className="text-white/30 text-[10px] italic">Click any day to set a per-date special rate (Diwali, NYE, festivals).</p>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-5" data-testid="month-tabs">
            {MONTHS.map((m, i) => (
              <button
                key={m}
                onClick={() => setMonthIdx(i)}
                data-testid={`month-tab-${i + 1}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  monthIdx === i ? 'bg-[#F57C20] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {m}
              </button>
            ))}
          </div>

          {/* Calendar */}
          {villa && (
            <div className="bg-[#1B2432] rounded-xl p-5" data-testid="calendar-grid">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {MONTHS_FULL[monthIdx]} <span className="text-[#F57C20]">{year}</span>
                </h2>
                <div className="flex items-center gap-3 text-[10px] text-white/40 flex-wrap">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F57C20]/40 inline-block" /> Weekend</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400/60 inline-block" /> Festival</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400/60 inline-block" /> Public holiday</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400/60 inline-block" /> Override</span>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1.5 mb-2">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                  <div key={d} className="text-center text-white/30 text-[10px] uppercase tracking-wider py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {grid.map((cell, i) => {
                  if (!cell) return <div key={i} className="aspect-square" />;
                  const overridden = overrides[cell.key] !== undefined;
                  const rate = effectiveRate(cell);
                  return (
                    <button
                      key={cell.key}
                      onClick={() => openDayEdit(cell)}
                      data-testid={`day-${cell.key}`}
                      className={`aspect-square text-left p-1.5 rounded-md border transition-all relative overflow-hidden
                        ${overridden ? 'bg-green-500/15 border-green-500/40 hover:bg-green-500/25'
                          : cell.holiday?.type === 'festival' ? 'bg-purple-500/10 border-purple-400/30 hover:bg-purple-500/20'
                          : cell.holiday?.type === 'public' ? 'bg-blue-500/10 border-blue-400/30 hover:bg-blue-500/20'
                          : cell.holiday?.type === 'travel-peak' ? 'bg-pink-500/15 border-pink-400/40 hover:bg-pink-500/25'
                          : cell.isWeekend ? 'bg-[#F57C20]/8 border-[#F57C20]/20 hover:bg-[#F57C20]/15'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                    >
                      <div className="flex items-start justify-between">
                        <span className={`text-sm font-bold ${cell.isWeekend ? 'text-[#F57C20]' : 'text-white'}`}>{cell.d}</span>
                        {overridden && <span className="text-[8px] text-green-400 font-bold leading-none mt-0.5">★</span>}
                      </div>
                      <p className={`text-[9px] mt-0.5 font-semibold leading-tight ${overridden ? 'text-green-300' : 'text-white/60'}`}>
                        Rs.{rate ? (rate / 1000).toFixed(1) + 'k' : '—'}
                      </p>
                      {cell.holiday && (
                        <p className="text-[8px] text-white/70 leading-tight truncate mt-0.5" title={cell.holiday.name}>{cell.holiday.name}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Toast */}
      {toast && (
        <div
          data-testid="rate-toast"
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-xs font-semibold shadow-lg ${
            toast.type === 'error'
              ? 'bg-red-500/15 text-red-300 border border-red-500/30'
              : 'bg-green-500/15 text-green-300 border border-green-500/30'
          }`}
        >
          {toast.text}
        </div>
      )}

      {/* Per-day modal */}
      {editDay && villa && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#1B2432] border border-white/10 rounded-xl max-w-md w-full p-6 relative" data-testid="day-edit-modal">
            <button onClick={() => setEditDay(null)} className="absolute top-3 right-3 text-white/40 hover:text-white" data-testid="day-edit-close">
              <FiX className="w-5 h-5" />
            </button>
            <p className="text-[#F57C20] text-[10px] tracking-[0.2em] uppercase font-semibold mb-2">Override rate for one date</p>
            <h3 className="text-white text-lg font-light mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              {new Date(editDay.key).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>
            <p className="text-white/40 text-xs mb-4">
              {villa.short_name} ({villa.bedrooms}BHK)
              {editDay.holiday && <span className="text-purple-300 ml-2">· {editDay.holiday.name}</span>}
              {editDay.isWeekend && !editDay.holiday && <span className="text-[#F57C20] ml-2">· Weekend</span>}
            </p>
            <div className="bg-white/5 rounded-lg p-3 mb-4 text-xs">
              <div className="flex justify-between text-white/60 mb-1">
                <span>Base rate ({editDay.isWeekend ? 'weekend' : 'weekday'})</span>
                <span className="text-white">Rs.{baseRateFor(editDay)?.toLocaleString()}</span>
              </div>
              {overrides[editDay.key] !== undefined && (
                <div className="flex justify-between text-green-400">
                  <span>Current override</span>
                  <span className="font-bold">Rs.{overrides[editDay.key].toLocaleString()}</span>
                </div>
              )}
            </div>
            <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">New rate for this date</label>
            <input
              type="number"
              value={draftRate}
              onChange={e => setDraftRate(e.target.value)}
              autoFocus
              data-testid="day-edit-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-base focus:outline-none focus:border-[#F57C20] mb-3"
            />
            <div className="flex gap-2 justify-end">
              {overrides[editDay.key] !== undefined && (
                <button onClick={clearDayOverride} disabled={busy} data-testid="day-edit-clear" className="bg-red-500/10 text-red-400 border border-red-500/30 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-red-500/20">
                  Clear override
                </button>
              )}
              <button onClick={() => setEditDay(null)} className="bg-white/5 text-white/70 hover:text-white border border-white/10 px-4 py-2 rounded-lg text-xs font-semibold">
                Cancel
              </button>
              <button onClick={saveDayEdit} disabled={busy} data-testid="day-edit-save" className="bg-[#F57C20] text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-[#e06b15] disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
            {editDay.holiday && (
              <p className="text-[10px] text-white/30 italic mt-3 text-center">
                Reminder: This day is {editDay.holiday.name} ({editDay.holiday.type}) — consider a peak rate.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Warning banner — always present */}
      <div className="fixed bottom-6 left-6 z-40 bg-[#1B2432] border border-[#F57C20]/30 rounded-lg p-3 max-w-xs shadow-xl">
        <p className="text-[#F57C20] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 mb-1">
          <FiAlertTriangle className="w-3 h-3" /> Existing bookings unaffected
        </p>
        <p className="text-white/50 text-[10px] leading-relaxed">
          Every booking snapshots its rates at payment. Changes here apply only to NEW bookings going forward.
        </p>
      </div>
    </div>
  );
};

export default RateManagement;
